import { SatelliteGroup, CloseApproachConfig } from '../types/satellite';
import { GROUP_PRESETS } from '../config/satelliteGroups';

interface ControlPanelProps {
  // Satellite loading
  currentGroup: SatelliteGroup;
  onGroupChange: (group: SatelliteGroup) => void;
  maxSatellites: number;
  onMaxSatellitesChange: (max: number) => void;
  onReload: () => void;
  loading: boolean;
  satelliteCount: number;
  
  // Visualization
  showTracks: boolean;
  onShowTracksChange: (show: boolean) => void;
  
  // Close approach analysis
  caConfig: CloseApproachConfig;
  onCAConfigChange: (config: CloseApproachConfig) => void;
  onRunCA: () => void;
  caRunning: boolean;
  caEventCount: number;
  
  // Export functions
  onExportGeoJSON: () => void;
  onExportCA: () => void;
  
  // Error state
  error: string | null;
}

export function ControlPanel({
  currentGroup,
  onGroupChange,
  maxSatellites,
  onMaxSatellitesChange,
  onReload,
  loading,
  satelliteCount,
  showTracks,
  onShowTracksChange,
  caConfig,
  onCAConfigChange,
  onRunCA,
  caRunning,
  caEventCount,
  onExportGeoJSON,
  onExportCA,
  error,
}: ControlPanelProps) {
  
  return (
    <div className="p-4 flex flex-wrap gap-3 items-center bg-black/60 text-white">
      {/* Satellite Group Selection */}
      <div className="flex items-center gap-2">
        <label className="text-sm opacity-80">Group</label>
        <select 
          value={currentGroup} 
          onChange={(e) => onGroupChange(e.target.value as SatelliteGroup)}
          className="bg-zinc-800 rounded px-2 py-1"
        >
          {GROUP_PRESETS.map((preset) => (
            <option key={preset.id} value={preset.id} title={preset.description}>
              {preset.label}
            </option>
          ))}
        </select>
        <button 
          onClick={onReload} 
          disabled={loading}
          className="ml-2 px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50"
        >
          {loading ? "Loading…" : "Reload"}
        </button>
      </div>
      
      {/* Max Satellites */}
      <div className="flex items-center gap-2">
        <label className="text-sm opacity-80">Max sats</label>
        <input 
          type="number" 
          value={maxSatellites} 
          onChange={(e) => onMaxSatellitesChange(Number(e.target.value))}
          min="100"
          max="5000"
          step="100"
          className="w-24 bg-zinc-800 rounded px-2 py-1" 
        />
        <span className="text-sm opacity-70">Loaded: {satelliteCount}</span>
      </div>
      
      {/* Ground Tracks Toggle */}
      <div className="flex items-center gap-2">
        <label className="text-sm opacity-80">Ground tracks</label>
        <input 
          type="checkbox" 
          checked={showTracks} 
          onChange={(e) => onShowTracksChange(e.target.checked)}
          className="rounded"
        />
      </div>
      
      {/* Export Controls */}
      <div className="flex items-center gap-2">
        <button 
          onClick={onExportGeoJSON}
          disabled={satelliteCount === 0}
          className="px-3 py-1 rounded bg-sky-600 hover:bg-sky-500 disabled:opacity-50"
        >
          Download GeoJSON
        </button>
      </div>
      
      {/* N2YO API Status */}
      <div className="flex items-center gap-2">
        <span className="text-sm opacity-80">N2YO API:</span>
        <span className="text-xs px-2 py-1 rounded bg-green-600/20 text-green-400">
          ✓ Active
        </span>
      </div>
      
      {/* Close Approach Controls */}
      <div className="flex items-center gap-2">
        <label className="text-sm opacity-80">CA thresh (km)</label>
        <input 
          type="number" 
          value={caConfig.thresholdKm} 
          onChange={(e) => onCAConfigChange({
            ...caConfig,
            thresholdKm: Number(e.target.value)
          })}
          min="1"
          max="100"
          className="w-20 bg-zinc-800 rounded px-2 py-1" 
        />
        
        <label className="text-sm opacity-80">Horizon (s)</label>
        <input 
          type="number" 
          value={caConfig.horizonSec} 
          onChange={(e) => onCAConfigChange({
            ...caConfig,
            horizonSec: Number(e.target.value)
          })}
          min="60"
          max="7200"
          step="60"
          className="w-24 bg-zinc-800 rounded px-2 py-1" 
        />
        
        <button 
          onClick={onRunCA} 
          disabled={caRunning || satelliteCount === 0}
          className="px-3 py-1 rounded bg-amber-600 hover:bg-amber-500 disabled:opacity-50"
        >
          {caRunning ? "Scanning…" : "Scan CA"}
        </button>
        
        <button 
          onClick={onExportCA} 
          disabled={caEventCount === 0}
          className="px-3 py-1 rounded bg-purple-600 hover:bg-purple-500 disabled:opacity-50"
        >
          Export CA
        </button>
        
        <span className="text-sm opacity-70">Events: {caEventCount}</span>
      </div>
      
      {/* Error Display */}
      {error && (
        <div className="text-red-400 text-sm flex-1">
          {error}
        </div>
      )}
    </div>
  );
}